import type { RefObject } from 'react';
import { action, computed, observable } from 'mobx';
import type { MapRef } from 'react-map-gl/mapbox';
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM_INDEX,
  MAP_ZOOM_LEVELS
} from '~/constants';
import type { POI } from '~/layouts/Base/types';
import { Position, type PositionType } from '~/ui/Sheet';
import { syncQueryParam } from '~/utils/state';
import booking from './booking';

// The camera center is mirrored into the URL (?lat&lon) as the map moves, so
// a copied URL reopens the map at the same spot. Read once at startup.
function readCenterFromUrl() {
  if (typeof window === 'undefined') return null;
  const params = new URL(window.location.href).searchParams;
  const latitude = parseFloat(params.get('lat') ?? '');
  const longitude = parseFloat(params.get('lon') ?? '');
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { longitude, latitude };
}

export class MapStore {
  // Imperative map handle, used to drive camera moves on zoom. Not observable.
  mapRef: RefObject<MapRef | null> | null = null;
  @observable accessor zoomIndex = DEFAULT_MAP_ZOOM_INDEX;

  // The scrim dims the map view while the tata animation is on screen; the
  // animation drives it, so pages without the animation never show it.
  @observable accessor scrimVisible = false;

  // How much of the viewport the sheet on screen covers. Not the source of
  // truth — each page owns its sheets' positions and reports the active one
  // here so the camera can pad around it (see Map's paddingForPosition).
  @observable accessor sheetPosition: PositionType = Position.None;

  // What a map-marker click does (which sheet opens, and where) is the
  // page's concern, so the page on screen registers a handler — like
  // setMapRef, but in the other direction. Not observable.
  private poiMarkerClickHandler: ((poi: POI) => void) | null = null;

  // Center shared via the URL. Read once at startup and only cleared by
  // resetCenter(), so the camera doesn't chase the mirrored lat/lon while the
  // user pans.
  @observable private accessor sharedCenter = readCenterFromUrl();

  setMapRef = (ref: RefObject<MapRef | null> | null) => {
    this.mapRef = ref;
  };

  @action.bound
  setSheetPosition(position: PositionType) {
    this.sheetPosition = position;
  }

  @action.bound
  setPoiMarkerClickHandler(handler: ((poi: POI) => void) | null) {
    this.poiMarkerClickHandler = handler;
  }

  handlePoiMarkerClick = (poi: POI) => {
    this.poiMarkerClickHandler?.(poi);
  };

  @action.bound
  setScrimVisible(visible: boolean) {
    this.scrimVisible = visible;
  }

  @computed
  get center() {
    if (this.sharedCenter) return this.sharedCenter;
    const location = booking.booking?.activitySummary?.places?.[0]?.location;
    if (location?.longitude == null || location?.latitude == null) {
      return DEFAULT_MAP_CENTER;
    }
    return { longitude: location.longitude, latitude: location.latitude };
  }

  // Forget the URL-shared center so the camera follows the booking's main
  // POI again — used when switching bookings, where the new location only
  // arrives once the booking loads.
  @action.bound
  clearSharedCenter() {
    this.sharedCenter = null;
  }

  // Return the camera to the main POI's location and default zoom (e.g. when
  // the header logo is clicked). The map is uncontrolled while panning, so the
  // ease has to be driven imperatively — the declarative center only applies on
  // prop changes.
  @action.bound
  resetCenter() {
    this.sharedCenter = null;
    this.zoomIndex = DEFAULT_MAP_ZOOM_INDEX;
    this.mapRef?.current?.easeTo({
      center: [this.center.longitude, this.center.latitude],
      zoom: MAP_ZOOM_LEVELS[DEFAULT_MAP_ZOOM_INDEX],
      duration: 800
    });
  }

  // Mirror the current camera center into the URL so the view can be shared.
  syncCenterToUrl = () => {
    const center = this.mapRef?.current?.getCenter();
    if (!center) return;
    syncQueryParam('lat', center.lat.toFixed(6));
    syncQueryParam('lon', center.lng.toFixed(6));
  };

  @computed
  get canZoomIn() {
    return this.zoomIndex < MAP_ZOOM_LEVELS.length - 1;
  }

  @computed
  get canZoomOut() {
    return this.zoomIndex > 0;
  }

  private goToZoomIndex(index: number) {
    this.zoomIndex = index;
    this.mapRef?.current?.easeTo({
      zoom: MAP_ZOOM_LEVELS[index],
      duration: 300
    });
  }

  @action.bound
  zoomIn() {
    if (this.canZoomIn) this.goToZoomIndex(this.zoomIndex + 1);
  }

  @action.bound
  zoomOut() {
    if (this.canZoomOut) this.goToZoomIndex(this.zoomIndex - 1);
  }

  // Sync zoomIndex to the discrete level nearest to the map's current zoom,
  // so the +/- buttons and ZoomScale stay consistent after a pinch gesture.
  // Icons and POI types also react to zoomIndex
  @action.bound
  syncZoomIndexFromMap(zoom: number) {
    let nearest = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < MAP_ZOOM_LEVELS.length; i++) {
      const distance = Math.abs(MAP_ZOOM_LEVELS[i] - zoom);
      if (distance < bestDistance) {
        bestDistance = distance;
        nearest = i;
      }
    }
    if (nearest !== this.zoomIndex) this.zoomIndex = nearest;
  }
}

export default new MapStore();
